import { useState } from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/Base/Popover";
import { Icon } from "@/components/Common/Icon";
import { IconNames } from "@/types/v2";
import { Button } from "@/components/Base/Button";
import { Combobox } from "@/components/Base/Combobox";
import { cn } from "@/lib/utils";

export type LayoutSelectorProps = {
  layouts: string[];
  selectedLayout: string | undefined;
  onLayoutChange: (value: string) => void;
  hideSearch?: boolean;
  disabled?: boolean;
  className?: string;
};

export const LayoutSelector = ({
  layouts,
  selectedLayout,
  onLayoutChange,
  hideSearch = false,
  disabled = false,
  className,
}: LayoutSelectorProps) => {
  const [isOpen, setIsOpen] = useState(false);

  if (hideSearch) {
    const handleLayoutChange = (value: string) => {
      setIsOpen(false);
      onLayoutChange(value);
    };

    return (
      <Popover
        open={isOpen && !disabled}
        onOpenChange={(open) => {
          if (!disabled) setIsOpen(open);
        }}
      >
        <PopoverTrigger
          disabled={disabled}
          className={cn(
            "relative flex h-9 items-center rounded-md p-2 text-sm font-semibold hover:bg-slate-200 disabled:cursor-not-allowed disabled:opacity-50",
            className
          )}
        >
          <Icon name={IconNames.layouts} className="mr-2" size={20} />{" "}
          {selectedLayout}
        </PopoverTrigger>
        <PopoverContent className="flex max-h-64 flex-col gap-2 overflow-y-auto p-2">
          {layouts.map((layout) => (
            <Button
              variant={"primary-alt"}
              key={layout}
              onClick={() => handleLayoutChange(layout)}
            >
              {layout}
            </Button>
          ))}
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <Combobox
      options={layouts.map((layout) => ({ value: layout, label: layout }))}
      value={selectedLayout}
      onValueChange={(value) => {
        // Combobox toggles a re-clicked option to "" (deselect); a layout
        // selector has no meaningful "no layout selected" state, so ignore it.
        if (value) onLayoutChange(value);
      }}
      placeholder="Select a layout..."
      emptyText="No layout found."
      disabled={disabled}
      className={className}
    />
  );
};
